import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { courses, courseCategories, type CourseCategory } from '../../data/courses';
import MatrixBackground from '../../components/ui/MatrixBackground';

const badgeColors: Record<string, string> = {
  green: 'bg-green-500/15 text-green-400',
  yellow: 'bg-yellow-500/15 text-yellow-400',
  purple: 'bg-purple-500/15 text-purple-400',
  blue: 'bg-blue-500/15 text-blue-400',
};

export default function Courses() {
  const [activeCategory, setActiveCategory] = useState<CourseCategory | null>(null);

  const nonBootcamp = courses.filter(c => c.category !== 'bootcamp');

  const filtered = activeCategory
    ? nonBootcamp.filter(c => c.category === activeCategory)
    : nonBootcamp;

  const countByCategory = (cat: CourseCategory) => nonBootcamp.filter(c => c.category === cat).length;

  return (
    <div className="min-h-screen bg-black font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-green-500/30 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2 text-green-400/70 hover:text-green-300 transition-colors text-sm font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
              Volver al inicio
            </Link>
            <Link to="/profile" className="text-green-400/70 hover:text-green-300 transition-colors text-sm font-medium">
              Mi perfil
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-28 pb-10 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-extrabold tracking-tight mb-3">
            <span className="text-white">Cursos</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-base md:text-lg text-gray-300 max-w-xl mx-auto">
            Cursos intensivos para acelerar tu carrera en Data Engineering
          </motion.p>
        </div>
      </section>

      {/* Content: Sidebar + Cards */}
      <section className="relative pb-24 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 px-4 lg:px-8 xl:px-12 flex gap-8">

          {/* Sidebar — desktop */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-24 self-start">
            <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-4">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Categorías</h3>
              <button
                onClick={() => setActiveCategory(null)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1 ${
                  activeCategory === null
                    ? 'bg-green-600/20 text-green-400'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                Todos
                <span className="ml-2 text-xs text-gray-500">{courses.length}</span>
              </button>
              {courseCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1 ${
                    activeCategory === cat.id
                      ? 'bg-green-600/20 text-green-400'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  {cat.label}
                  <span className="ml-2 text-xs text-gray-500">{countByCategory(cat.id)}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* Mobile filter pills */}
          <div className="lg:hidden flex flex-wrap gap-2 mb-6 w-full">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                activeCategory === null
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Todos
            </button>
            {courseCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Cards grid */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {filtered.map((course, i) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/cursos/${course.id}`}
                  className="group flex flex-col h-full bg-gray-900 rounded-lg overflow-hidden shadow-xl transition-all duration-300 ease-in-out hover:-translate-y-1 hover:ring-2 hover:ring-green-500/40"
                >
                  {/* Image area */}
                  <div className="relative">
                    <div className={`h-44 bg-gradient-to-br ${course.gradient} flex items-center justify-center`}>
                      <span className="text-6xl font-black text-white/[0.07] select-none group-hover:text-white/[0.12] transition-colors duration-300">
                        {course.iconText}
                      </span>
                    </div>
                    <div className="absolute right-0 top-0 h-8 flex items-center gap-1 rounded-bl-md bg-gray-900 px-2 pt-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 14 18" fill="#facc15">
                        <path d="M12.672 8.2c-.23-.3-.51-.56-.77-.82-.67-.6-1.43-1.03-2.07-1.66C8.342 4.26 8.012 1.85 8.962 0c-.95.23-1.78.75-2.49 1.32-2.59 2.08-3.61 5.75-2.39 8.9.04.1.08.2.08.33 0 .22-.15.42-.35.5-.23.1-.47.04-.66-.12a.58.58 0 0 1-.14-.17c-1.13-1.43-1.31-3.48-.55-5.12C.792 7-.118 9.3.012 11.47c.06.5.12 1 .29 1.5.14.6.41 1.2.71 1.73 1.08 1.73 2.95 2.97 4.96 3.22 2.14.27 4.43-.12 6.07-1.6 1.83-1.66 2.47-4.32 1.53-6.6l-.13-.26c-.21-.46-.77-1.26-.77-1.26Zm-3.16 6.3c-.28.24-.74.5-1.1.6-1.12.4-2.24-.16-2.9-.82 1.19-.28 1.9-1.16 2.11-2.05.17-.8-.15-1.46-.28-2.23-.12-.74-.1-1.37.17-2.06.19.38.39.76.63 1.06.77 1 1.98 1.44 2.24 2.8.04.14.06.28.06.43.03.82-.33 1.72-.93 2.27Z" />
                      </svg>
                      <span className="text-sm font-semibold text-yellow-400">{course.totalClasses * 25}</span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col justify-between gap-3 px-5 pb-5 pt-3">
                    <div>
                      <div className="inline">
                        <span className="align-middle text-lg font-bold text-green-400 group-hover:underline">{course.title}</span>
                        {' '}
                        <span className={`inline-block rounded px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider ${badgeColors[course.badgeColor]}`}>
                          {course.badge}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pb-4 pt-2">
                      <span className="flex items-center gap-1.5 rounded-full bg-gray-800 px-3 py-1.5 text-xs font-medium text-white">
                        {course.totalClasses} Clases
                      </span>
                      <span className="flex items-center gap-1.5 rounded-full bg-gray-800 px-3 py-1.5 text-xs font-medium text-white">
                        {course.totalModules} Módulos
                      </span>
                    </div>

                    <div className="mt-auto flex w-full items-center justify-end">
                      <span className="inline-flex items-center gap-2 rounded-md bg-green-500 px-4 py-2 text-sm font-semibold text-black transition-colors group-hover:bg-green-400">
                        Ver más
                        <svg className="w-4 h-4 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth={2.5}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}

            {filtered.length === 0 && (
              <div className="w-full text-center py-16">
                <p className="text-gray-500 text-lg">No hay cursos en esta categoría todavía.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
